import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import RoleShell from '../../components/Enrollment/RoleShell';
import { Prod01 } from '../../constants/ActionType';
import { useSortableTable, SortableTh } from '../../components/SortableTable';

// Super Admin: per-practice Medicare locality (for CY2026 MPFS rates) and TDW agreement (effective-dated, audited).
// Accounting sees the same page read-only. Everyone else gets 403 from the server.
const PRACTICE_SORT_COLUMNS = {
    practice: { type: 'string', value: (o) => o?.name },
    locality: { type: 'string', value: (o) => o?.mpfsLocality },
};
const errText = (e) => e?.response?.data?.message || e?.message || 'Request failed';
const usd = (n) => `$${Number(n).toFixed(2)}`;
const terms = (a) => (a.feeModel === 'flat_per_patient_month'
    ? `${usd(a.flatAmount)} per billable patient-month`
    : `TDW ${a.tdwPercent}% / practice ${a.practicePercent}% of collections`);

const AgreementForm = ({ org, defaults, onSaved }) => {
    const [f, setF] = useState({
        effectiveFrom: moment().add(1, 'month').startOf('month').format('YYYY-MM-DD'),
        feeModel: defaults.feeModel,
        tdwPercent: defaults.tdwPercent,
        flatAmount: '',
        patientCollectionRate: defaults.patientCollectionRate,
        note: '',
    });
    const [error, setError] = useState(null);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const id = (k) => `agr-${org.organizationId}-${k}`;
    const save = async () => {
        setError(null);
        try { await axios.post(`${Prod01}/accounting/practice-settings/${org.organizationId}/agreement`, f); onSaved(); } catch (e) { setError(errText(e)); }
    };
    return (
        <div className="border rounded p-2 mt-2" style={{ fontSize: 13 }} data-testid="agreement-form">
            <div className="form-row">
                <div className="col-md-2"><label htmlFor={id('from')}>Effective from</label><input id={id('from')} type="date" className="form-control form-control-sm" value={f.effectiveFrom} onChange={set('effectiveFrom')} /></div>
                <div className="col-md-3"><label htmlFor={id('model')}>Fee model</label>
                    <select id={id('model')} className="form-control form-control-sm" value={f.feeModel} onChange={set('feeModel')}>
                        <option value="percent_of_collections">Percent of collections</option>
                        <option value="flat_per_patient_month">Flat $ per billable patient-month</option>
                    </select>
                </div>
                {f.feeModel === 'percent_of_collections'
                    ? <div className="col-md-2"><label htmlFor={id('pct')}>TDW %</label><input id={id('pct')} type="number" min="0" max="100" className="form-control form-control-sm" value={f.tdwPercent} onChange={set('tdwPercent')} /></div>
                    : <div className="col-md-2"><label htmlFor={id('flat')}>Flat $</label><input id={id('flat')} type="number" min="0" step="0.01" className="form-control form-control-sm" value={f.flatAmount} onChange={set('flatAmount')} /></div>}
                <div className="col-md-2"><label htmlFor={id('coll')}>Patient collection %</label><input id={id('coll')} type="number" min="0" max="100" className="form-control form-control-sm" value={f.patientCollectionRate} onChange={set('patientCollectionRate')} /></div>
                <div className="col-md-3"><label htmlFor={id('note')}>Note</label><input id={id('note')} className="form-control form-control-sm" value={f.note} onChange={set('note')} /></div>
            </div>
            {error && <small className="text-danger d-block">{error}</small>}
            <button type="button" className="btn btn-primary btn-sm mt-2" onClick={save}>Add agreement</button>
        </div>
    );
};

const PracticeSettings = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState({});
    const load = useCallback(async () => {
        setError(null);
        try { setData((await axios.get(`${Prod01}/accounting/practice-settings`)).data); } catch (e) { setError(errText(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const tableSort = useSortableTable(data?.practices || [], { id: 'accounting.practiceSettings', columns: PRACTICE_SORT_COLUMNS });
    const setLocality = async (org, mpfsLocality) => {
        setError(null);
        try { await axios.post(`${Prod01}/accounting/practice-settings/${org.organizationId}/locality`, { mpfsLocality: mpfsLocality || null }); load(); } catch (e) { setError(errText(e)); }
    };
    return (
        <RoleShell title="Practice billing settings">
            <div className="container-fluid mt-3" data-testid="practice-settings">
                <h5>Practice billing settings</h5>
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {data && (
                    <>
                        <p className="text-muted" style={{ fontSize: 12 }}>
                            Medicare fallback rates: {data.mpfsSource.source} (effective {data.mpfsSource.effectiveYear}), non-facility, by the practice&apos;s Florida MAC locality.
                            A practice without a locality gets &quot;No rate loaded&quot; for Medicare rows that have no contract rate — never a national rate.
                            {data.canEdit ? ' Every change is audited.' : ' Read-only.'}
                        </p>
                        <table className="table table-sm" style={{ fontSize: 13 }}>
                            <thead><tr><SortableTh sortKey="practice" sort={tableSort}>Practice</SortableTh><SortableTh sortKey="locality" sort={tableSort}>Medicare locality</SortableTh><th>TDW agreement in force</th><th>Patient collection</th><th /></tr></thead>
                            <tbody>
                                {tableSort.rows.map((o) => (
                                    <React.Fragment key={o.organizationId}>
                                        <tr data-testid="ps-row">
                                            <td>{o.name}</td>
                                            <td>
                                                {data.canEdit ? (
                                                    <select aria-label={`Locality for ${o.name}`} className="form-control form-control-sm" value={o.mpfsLocality || ''} onChange={(e) => setLocality(o, e.target.value)}>
                                                        <option value="">Not set</option>
                                                        {data.localities.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                                                    </select>
                                                ) : (o.localityLabel || <span className="text-danger">Not set</span>)}
                                            </td>
                                            <td>{terms(o.current)}{o.current.isDefault ? <small className="d-block text-muted">default — no agreement entered</small> : <small className="d-block text-muted">since {o.current.effectiveFrom}</small>}</td>
                                            <td>{o.current.patientCollectionRate}%</td>
                                            <td><button type="button" className="btn btn-link btn-sm p-0" onClick={() => setOpen({ ...open, [o.organizationId]: !open[o.organizationId] })}>{open[o.organizationId] ? 'Hide' : 'History'}{data.canEdit ? ' / add' : ''}</button></td>
                                        </tr>
                                        {open[o.organizationId] && (
                                            <tr><td colSpan={5} style={{ background: '#fafafa' }}>
                                                {o.agreements.length ? (
                                                    <ul className="mb-0" style={{ fontSize: 12 }}>
                                                        {o.agreements.map((a) => <li key={a.id}>From {a.effectiveFrom}: {terms(a)}, patient collection {a.patientCollectionRate}% — set by {a.setByName || 'unknown'}{a.note ? ` (${a.note})` : ''}</li>)}
                                                    </ul>
                                                ) : <small>No agreements entered; defaults apply.</small>}
                                                {data.canEdit && <AgreementForm org={o} defaults={data.defaults} onSaved={load} />}
                                            </td></tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </RoleShell>
    );
};

export default PracticeSettings;
